import { useCallback, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFlex from 'UIComponents/layout/UIFlex';
import { isFunction } from '../../utils/TypeUtils';
import { ActionIconButton } from './internal/ActionIconButton';
import IconWithClickAnimation from './internal/IconWithClickAnimation';
import CopyMessageActionButton from './internal/CopyMessageActionButton';
import InlineFeedbackPopover from './internal/InlineFeedbackPopover';
import { useTracker } from '../../hooks/useTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const POSITIVE_FEEDBACK = 1;
const NEGATIVE_FEEDBACK = -1;
const InlineFeedback = ({
  hideCopyButton = false,
  copyMessage,
  isFeedbackDisabled = false,
  messageId,
  onPositiveFeedback,
  onNegativeFeedback,
  usageTracker,
  usageTrackerAppLocationKey,
  initialVote,
  negativeFeedbackPopoverTitle,
  negativeFeedbackPopoverBody,
  negativeFeedbackPopoverBodyOptions,
  negativeFeedbackPopoverTextAreaPlaceholder,
  threadId = 0
}) => {
  const {
    aiTracker
  } = useTracker(usageTracker, usageTrackerAppLocationKey);
  const [draftVote, setDraftVote] = useState(initialVote);
  const [feedbackPopoverOpen, setFeedbackPopoverOpen] = useState(false);
  const handlePositiveFeedback = useCallback(() => {
    const isPositiveFeedbackSelected = draftVote === POSITIVE_FEEDBACK;
    if (isPositiveFeedbackSelected) {
      if (aiTracker) {
        aiTracker.track('feedbackInteraction', {
          action: 'prompt_upvote_removed'
        });
      }
      setDraftVote(null);
    } else {
      if (aiTracker) {
        aiTracker.track('feedbackInteraction', {
          action: 'prompt_upvoted'
        });
      }
      setDraftVote(POSITIVE_FEEDBACK);
    }
    if (isFunction(onPositiveFeedback)) {
      onPositiveFeedback({
        messageId,
        threadId,
        vote: 1
      });
    }
  }, [draftVote, messageId, onPositiveFeedback, aiTracker, threadId]);
  const handleNegativeFeedbackClick = useCallback(() => {
    const isNegativeFeedbackSelected = draftVote === NEGATIVE_FEEDBACK;
    if (isNegativeFeedbackSelected) {
      if (aiTracker) {
        aiTracker.track('feedbackInteraction', {
          action: 'prompt_downvote_removed'
        });
      }
      setDraftVote(null);
    } else {
      if (aiTracker) {
        aiTracker.track('feedbackInteraction', {
          action: 'prompt_downvoted'
        });
      }
      setDraftVote(NEGATIVE_FEEDBACK);
      setFeedbackPopoverOpen(true);
    }
  }, [draftVote, aiTracker]);
  const handleClose = useCallback(() => {
    setFeedbackPopoverOpen(false);
  }, []);
  const handleSubmit = ({
    options,
    message: feedbackMessage
  }) => {
    setFeedbackPopoverOpen(false);
    if (isFunction(onNegativeFeedback)) {
      onNegativeFeedback({
        messageId,
        threadId,
        options,
        feedbackMessage,
        vote: -1
      });
    }
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(UIFlex, {
      "data-test-id": "inline-feedback",
      children: [/*#__PURE__*/_jsx(ActionIconButton, {
        "aria-pressed": draftVote === POSITIVE_FEEDBACK,
        "data-test-id": "positive-feedback-button",
        disabled: isFeedbackDisabled,
        selected: draftVote === POSITIVE_FEEDBACK,
        onClick: handlePositiveFeedback,
        placement: "bottom",
        tooltip: isFeedbackDisabled ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "aiComponentsUiLibrary.components.inlineFeedback.disabledFeedback.tooltip"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "aiComponentsUiLibrary.components.inlineFeedback.positiveFeedback.tooltip"
        }),
        use: "tertiary-extra-light",
        size: "sm",
        children: /*#__PURE__*/_jsx(IconWithClickAnimation, {
          name: "thumbsUp"
        })
      }), /*#__PURE__*/_jsx(InlineFeedbackPopover, {
        popoverOpen: feedbackPopoverOpen,
        onSubmit: handleSubmit,
        onCancel: handleClose,
        popoverTitle: negativeFeedbackPopoverTitle,
        popoverBody: negativeFeedbackPopoverBody,
        negativeFeedbackPopoverBodyOptions: negativeFeedbackPopoverBodyOptions,
        textAreaPlaceholder: negativeFeedbackPopoverTextAreaPlaceholder,
        children: /*#__PURE__*/_jsx(ActionIconButton, {
          "aria-pressed": draftVote === NEGATIVE_FEEDBACK,
          "data-test-id": "negative-feedback-button",
          disabled: isFeedbackDisabled,
          selected: draftVote === NEGATIVE_FEEDBACK,
          onClick: handleNegativeFeedbackClick,
          placement: "bottom",
          tooltip: isFeedbackDisabled ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "aiComponentsUiLibrary.components.inlineFeedback.disabledFeedback.tooltip"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "aiComponentsUiLibrary.components.inlineFeedback.negativeFeedback.tooltip"
          }),
          use: "tertiary-extra-light",
          size: "sm",
          children: /*#__PURE__*/_jsx(IconWithClickAnimation, {
            name: "thumbsDown"
          })
        })
      }), !hideCopyButton && copyMessage && /*#__PURE__*/_jsx(CopyMessageActionButton, {
        message: copyMessage,
        usageTracker: aiTracker
      })]
    })
  });
};
export default InlineFeedback;